import React, { useCallback, useEffect, useState, FC } from 'react'

import clsx from 'clsx'
import styled from 'styled-components'

import { Icon } from 'src/components/atoms'

const Container = styled.div<{ isFocus: boolean }>`
  background: #ffffff;
  box-shadow: inset 0 0.2rem 0.4rem rgba(0, 0, 0, 0.05);
  border-radius: 2rem;
  transition: 0.2s ease-in-out;

  &:hover:not(:focus):not(.error):not(:disabled) {
    box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.1);
  }
`
const Wrapper = styled.div`
  // tailwindを用いるとsafari系のブラウザでレイアウト崩れが起きる事があったためstyled-componentsで定義
  display: flex;
`
const StyledInput = styled.input`
  font-size: var(--r0);
  outline: none;
`

type Props = {
  value?: string
  placeholder?: string
  size?: 's' | 'm'
  onFocus?: () => void
  onBlur?: () => void
  onChange: (value: string) => void
  onClickEscapeKey?: () => void
  className?: string
}

export const Search: FC<Props> = ({
  value,
  placeholder,
  size = 'm',
  onFocus,
  onBlur,
  onChange,
  onClickEscapeKey,
  className,
}) => {
  const [isFocus, setIsFocus] = useState(false)
  const [timer, setTimer] = useState<any | null>(null)
  // IMEの入力中はtrueに
  const [imeInput, setImeInput] = useState(false)
  // 実際に表示されるvalueの値。valueをそのまま用いると IMEとtimer処理の影響でうまく動かないため useState を用いて再現する
  const [displayValue, setDisplayValue] = useState(value)

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(event.target.value)
    if (timer) {
      clearTimeout(timer)
      setTimer(null)
    }
    setTimer(
      setTimeout(() => {
        // IMEの入力が終わっている時のみ onChange に伝搬する
        // また頻繁に伝搬されないようtimerを用いる
        !imeInput && onChange(event.target.value)
      }, 200)
    )
  }

  useEffect(() => {
    // valueの値が外部から更新され、表示されている値と相違が出た際（別のアクションが元でvalueを空にした場合など）に表示する値も更新する
    if (displayValue !== value) {
      setDisplayValue(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // IMEを使った変換が開始された時
  const handleCompositionStart = () => {
    setImeInput(true)
  }
  // IMEを使った変換が終了した時
  const handleCompositionEnd = (event: React.CompositionEvent<HTMLInputElement>) => {
    setImeInput(false)
    // IME変換終了時に明示的に onChange を呼び出す
    onChange(event.currentTarget.value)
  }

  useEffect(() => {
    const input = document.querySelector('.searchInput') as HTMLInputElement

    // IMEの入力が終わった時に強制的にinputにeventを起こして handleInputChange を実行する
    // NOTE: 下記issueに書いてあるように React のバージョンによって書き方や実装方法が異なるためバージョン変更時には注意
    // 参考: https://github.com/facebook/react/issues/11488#issuecomment-884790146

    // @ts-expect-error NOTE: clear the interal value to force an actual change
    // eslint-disable-next-line no-underscore-dangle
    input._valueTracker?.setValue('')
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }, [imeInput])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (onClickEscapeKey && event.key === 'Escape') {
        onClickEscapeKey()

        const input = document.querySelector('.searchInput') as HTMLInputElement
        input.blur()
      }
    },
    [onClickEscapeKey]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false)
    // unmount時に eventListener を削除する事で何度も同じイベントが登録されてしまうのを防ぐ
    return () => document.removeEventListener('keydown', handleKeyDown, false)
  }, [handleKeyDown])

  return (
    <Container
      className={clsx(
        isFocus ? 'tw-border-roovPrimary-default' : 'tw-border-border-secondary',
        size === 's' ? 'tw-h-[2.14rem] tw-px-s0' : 'tw-h-[3.3rem] tw-px-s1',
        'tw-border focus:tw-border-[#060e6d]',
        className
      )}
      isFocus={isFocus}
    >
      <div className="tw-flex tw-items-center tw-h-full">
        <Wrapper className="tw-items-center tw-w-full">
          <Icon path="search/soft/line" className="tw-text-standardText-disable" />
          <StyledInput
            value={displayValue || ''}
            placeholder={placeholder}
            onFocus={() => {
              setIsFocus(true)
              onFocus && onFocus()
            }}
            onBlur={() => {
              setIsFocus(false)
              onBlur && onBlur()
            }}
            onChange={handleInputChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="tw-ml-s-2 tw-w-full searchInput tw-caret-correctGreen-default tw-font-normal"
          />
        </Wrapper>
      </div>
    </Container>
  )
}
